/**
 * Analytics Portal V1 — pitch-deck interest (Phase 13, plan §15 + decisions 4 and 8).
 *
 * Renders only the stable, status-independent metrics the server returns: submissions, unique
 * founders, new vs repeat submitters, the submission series and the server-suppressed submission
 * breakdowns. It never shows — and never reads — a status, review state, timing, file activity,
 * filename, deck content, submitter identity or any meeting attribution, and it never implies a
 * submission was reviewed or acted upon. Unique founders are aggregate counts only.
 */
import React from "react";
import { AudienceBreakdown } from "./AudienceBreakdown";
import { KpiCard } from "./KpiCard";
import { TrendChart } from "./TrendChart";
import { SUBMISSION_DIMENSION_LABEL } from "./capitalMetrics";

const CARDS = [
  { key: "submitted", label: "Pitch decks submitted" },
  { key: "unique_founders", label: "Unique founders submitting" },
  { key: "new_submitters", label: "New submitters" },
  { key: "repeat_submitters", label: "Repeat submitters" },
];

export const PitchDeckSection = ({ pitchDecks, rangeSuggestion, onUseSupportedRange,
                                   onChangeRange }) => {
  if (!pitchDecks) return null;
  const cards = CARDS.filter((card) => pitchDecks[card.key]);
  const series = (pitchDecks.series || []).map((point) => ({
    interval_start: point.interval_start,
    interval_type: point.interval_type,
    metrics: { submitted: point.submitted },
  }));

  return (
    <section className="space-y-3" data-testid="analytics-pitch-decks"
             aria-labelledby="analytics-pitch-decks-heading">
      <div>
        <h2 id="analytics-pitch-decks-heading"
            className="text-base font-[620] text-foreground md:text-lg">Pitch-deck interest</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          How many decks founders sent you, and who sent them — as aggregate counts only. Submission
          progress, outcomes and file activity are not reported.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <KpiCard key={card.key} metricKey={`pitch_${card.key}`} label={card.label}
                   metric={pitchDecks[card.key]} rangeSuggestion={rangeSuggestion}
                   onUseSupportedRange={onUseSupportedRange} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <TrendChart series={series} metricKey="submitted" title="Submissions over time"
                    unitLabel="submissions" testId="analytics-chart-submissions"
                    onChangeRange={onChangeRange} />
        <AudienceBreakdown audience={pitchDecks.breakdowns} title="Submitter breakdowns"
                           labels={SUBMISSION_DIMENSION_LABEL}
                           testId="analytics-submission-breakdowns"
                           description="Aggregate groupings the server already suppressed and merged — never an individual founder, business or location." />
      </div>
    </section>
  );
};
